import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import { ExpandMore, ChevronRight, FolderOpen, Folder, InsertDriveFile, Search, Close } from '@mui/icons-material';
import PatchViewer from './PatchViewer';

interface ManifestArtifactViewerProps {
  executionId: string;
  artifactIndex: number;
  artifactUrl: string;
  initialFile?: string | null;
  onFileSelect?: (file: string | null) => void;
}

function getArtifactFilename(url: string) {
  const parts = url.split('/');
  return parts[parts.length - 1];
}

function buildProxyPath(executionId: string, artifactIndex: number, artifactUrl: string, relativePath?: string) {
  const base = `/test-artifacts/${executionId}/${artifactIndex}`;
  if (relativePath) {
    return `${base}/${relativePath}`;
  }
  return `${base}/${getArtifactFilename(artifactUrl)}`;
}

interface TreeNode {
  name: string;
  fullPath: string;
  children: TreeNode[];
  isFile: boolean;
}

function buildFileTree(files: string[]): TreeNode[] {
  const root: TreeNode[] = [];

  for (const filePath of files) {
    const parts = filePath.split('/');
    let current = root;

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      const isFile = i === parts.length - 1;
      const fullPath = parts.slice(0, i + 1).join('/');

      let existing = current.find(n => n.name === part && n.isFile === isFile);
      if (!existing) {
        existing = { name: part, fullPath, children: [], isFile };
        current.push(existing);
      }
      current = existing.children;
    }
  }

  // Sort: folders first, then files, alphabetically within each group
  const sortNodes = (nodes: TreeNode[]) => {
    nodes.sort((a, b) => {
      if (a.isFile !== b.isFile) return a.isFile ? 1 : -1;
      return a.name.localeCompare(b.name);
    });
    nodes.forEach(n => sortNodes(n.children));
  };
  sortNodes(root);

  return root;
}

function FileTreeNode({
  node,
  selectedFile,
  onSelectFile,
  depth,
  defaultExpanded,
}: {
  node: TreeNode;
  selectedFile: string | null;
  onSelectFile: (path: string) => void;
  depth: number;
  defaultExpanded: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (node.isFile) {
    return (
      <ListItemButton
        selected={node.fullPath === selectedFile}
        onClick={() => onSelectFile(node.fullPath)}
        sx={{ py: 0.25, pl: depth * 2 + 1 }}
      >
        <InsertDriveFile sx={{ fontSize: 14, mr: 0.75, color: 'text.secondary', flexShrink: 0 }} />
        <ListItemText
          primary={node.name}
          primaryTypographyProps={{ variant: 'body2', fontFamily: 'monospace', fontSize: '0.8rem', noWrap: true }}
        />
      </ListItemButton>
    );
  }

  return (
    <>
      <ListItemButton onClick={() => setExpanded(e => !e)} sx={{ py: 0.25, pl: depth * 2 + 1 }}>
        {expanded ? <ExpandMore sx={{ fontSize: 16, mr: 0.5 }} /> : <ChevronRight sx={{ fontSize: 16, mr: 0.5 }} />}
        {expanded ? (
          <FolderOpen sx={{ fontSize: 16, mr: 0.75, color: 'text.secondary' }} />
        ) : (
          <Folder sx={{ fontSize: 16, mr: 0.75, color: 'text.secondary' }} />
        )}
        <ListItemText
          primary={node.name}
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600, fontSize: '0.8rem' }}
        />
      </ListItemButton>
      <Collapse in={expanded}>
        {node.children.map(child => (
          <FileTreeNode
            key={child.fullPath}
            node={child}
            selectedFile={selectedFile}
            onSelectFile={onSelectFile}
            depth={depth + 1}
            defaultExpanded={defaultExpanded}
          />
        ))}
      </Collapse>
    </>
  );
}

export default function ManifestArtifactViewer({ executionId, artifactIndex, artifactUrl, initialFile, onFileSelect }: ManifestArtifactViewerProps) {
  const [files, setFiles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<string | null>(null);
  const [fileLoading, setFileLoading] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);

  const [filter, setFilter] = useState('');

  const handleSelectFile = useCallback((file: string | null) => {
    setSelectedFile(file);
    onFileSelect?.(file);
  }, [onFileSelect]);

  const filteredFiles = useMemo(() => {
    if (!filter.trim()) return files;
    const lc = filter.toLowerCase();
    return files.filter(f => f.toLowerCase().includes(lc));
  }, [files, filter]);

  const fileTree = useMemo(() => buildFileTree(filteredFiles), [filteredFiles]);

  // Fetch manifest
  useEffect(() => {
    setLoading(true);
    setError(null);
    setSelectedFile(null);
    setFileContent(null);

    const url = buildProxyPath(executionId, artifactIndex, artifactUrl);
    fetch(url, { credentials: 'include' })
      .then(res => {
        if (!res.ok) throw new Error(`Failed to fetch manifest: ${res.status}`);
        return res.json();
      })
      .then((data: { files: string[] }) => {
        setFiles(data.files || []);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to load manifest:', err);
        setError(err.message);
        setLoading(false);
      });
  }, [executionId, artifactIndex, artifactUrl]);

  // Select initial file from URL hash once manifest loads
  const [initialFileHandled, setInitialFileHandled] = useState(false);
  useEffect(() => {
    if (initialFileHandled || !initialFile || files.length === 0) return;
    if (files.includes(initialFile)) {
      setInitialFileHandled(true);
      setSelectedFile(initialFile);
    }
  }, [files, initialFile, initialFileHandled]);

  // Fetch selected file
  useEffect(() => {
    if (!selectedFile) {
      setFileContent(null);
      return;
    }

    setFileLoading(true);
    setFileError(null);

    const url = buildProxyPath(executionId, artifactIndex, artifactUrl, selectedFile);
    fetch(url, { credentials: 'include' })
      .then(res => {
        if (!res.ok) throw new Error(`Failed to fetch file: ${res.status}`);
        return res.text();
      })
      .then(text => {
        setFileContent(text);
        setFileLoading(false);
      })
      .catch(err => {
        console.error('Failed to load file:', err);
        setFileError(err.message);
        setFileLoading(false);
      });
  }, [executionId, artifactIndex, artifactUrl, selectedFile]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* File tree sidebar */}
      <Box sx={{ width: 280, minWidth: 280, borderRight: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ p: 1, bgcolor: 'action.hover' }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Filter files..."
            value={filter}
            onChange={e => setFilter(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 18 }} />
                </InputAdornment>
              ),
              endAdornment: filter ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setFilter('')}>
                    <Close sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
              sx: { fontSize: '0.8rem' },
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {filteredFiles.length}{filter ? ` of ${files.length}` : ''} file{filteredFiles.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
        <List dense disablePadding sx={{ overflow: 'auto', flex: 1 }}>
          {fileTree.map(node => (
            <FileTreeNode
              key={node.fullPath}
              node={node}
              selectedFile={selectedFile}
              onSelectFile={handleSelectFile}
              depth={0}
              defaultExpanded={true}
            />
          ))}
        </List>
      </Box>

      {/* File content */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        {!selectedFile && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Typography color="text.secondary">Select a file to view its contents</Typography>
          </Box>
        )}

        {selectedFile && fileLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        )}

        {selectedFile && fileError && (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">{fileError}</Alert>
          </Box>
        )}

        {selectedFile && fileContent !== null && !fileLoading && (
          <PatchViewer content={fileContent} filename={selectedFile} />
        )}
      </Box>
    </Box>
  );
}
